// Shared helpers for exporting witness examination / accused interrogation
// questions (Copy to clipboard, Share via WhatsApp, Download as Word) - used
// by both the desktop InvestigateFIR tables and the mobile Questions screen.

// Roughly half of a standard page's usable height (11in page, ~1in top/bottom
// margins => ~9in usable; half of that is 4.5in = 6480 twips). Used to leave
// blank space under an unanswered question for the user to write in by hand.
export const HALF_PAGE_TWIPS = 6480;

export const hasAnyAnswers = (questions) =>
  Array.isArray(questions) && questions.some((q) => Array.isArray(q.answer) && q.answer.length > 0);

// Plain-text rendering used for "Copy to clipboard" (desktop) and "Share via
// WhatsApp" (mobile). Matches the pre-existing Copy format ("1. <question>")
// when answers aren't included, so enabling the Include Answers checkbox is
// purely additive.
export const buildQuestionsPlainText = (questions, includeAnswers) => {
  const list = Array.isArray(questions) ? questions : [];

  const blocks = list.map((q, idx) => {
    const lines = [`${idx + 1}. ${q.question}`];

    if (includeAnswers && Array.isArray(q.answer) && q.answer.length > 0) {
      lines.push('Answer:');
      q.answer.forEach((ans, i) => lines.push(`${i + 1}. ${ans}`));
    }

    return lines.join('\n');
  });

  // Single-newline join matches the original Copy behaviour when there are
  // no answers to show; once answers are included, a blank line between
  // questions keeps the block readable.
  return blocks.join(includeAnswers ? '\n\n' : '\n');
};

// Sentinel value for "don't filter by date" in the date dropdown / grouping.
export const ALL_DATES_OPTION = 'All Dates';

// answerDate is ddMMyyyy (e.g. "24082026") -> "24-08-2026".
export const formatAnswerDateLabel = (ddMMyyyy) => {
  if (!ddMMyyyy || ddMMyyyy.length !== 8) return '';
  return `${ddMMyyyy.slice(0, 2)}-${ddMMyyyy.slice(2, 4)}-${ddMMyyyy.slice(4, 8)}`;
};

const parseDdMmYyyy = (ddMMyyyy) => {
  const dd = Number(ddMMyyyy.slice(0, 2));
  const mm = Number(ddMMyyyy.slice(2, 4)) - 1;
  const yyyy = Number(ddMMyyyy.slice(4, 8));
  return new Date(yyyy, mm, dd).getTime();
};

// Unique answerDate values (as { raw: ddMMyyyy, label: dd-MM-yyyy }), sorted
// chronologically, across questions that have both a recorded answer and an
// answerDate. Questions with no answer have nothing to date - they're
// excluded here (and from date-wise export entirely).
export const getUniqueAnswerDates = (questions) => {
  const list = Array.isArray(questions) ? questions : [];
  const raws = new Set();

  list.forEach((q) => {
    const hasAnswer = Array.isArray(q.answer) && q.answer.length > 0;
    if (hasAnswer && q.answerDate) raws.add(q.answerDate);
  });

  return Array.from(raws)
    .sort((a, b) => parseDdMmYyyy(a) - parseDdMmYyyy(b))
    .map((raw) => ({ raw, label: formatAnswerDateLabel(raw) }));
};

// Builds the block list (see docxBuilder.createStructuredDocxBlob) for a
// Word document of questions, optionally with answers. Any question with no
// recorded answer gets a half-page blank paragraph reserved underneath it so
// the printed document can be filled in by hand.
//
// `heading` is the bold top-of-document line, e.g. "Examination of Jane Roe"
// or "Interrogation of John Doe" - always shown once (non-date-wise), or
// repeated at the top of every date group (date-wise).
//
// When `dateWise` is true (only meaningful together with `includeAnswers`):
//   - `selectedDate` === ALL_DATES_OPTION: one section per unique answerDate
//     found among `questions`, each starting on a new page, heading + date
//     line, then just that date's answered questions.
//   - `selectedDate` is a specific dd-MM-yyyy label: a single section with
//     only the questions answered on that date.
// Unanswered questions are excluded entirely from date-wise output, since
// they have no date to group under.
export const buildQuestionsDocxBlocks = ({
  heading,
  questions,
  includeAnswers,
  dateWise = false,
  selectedDate = ALL_DATES_OPTION,
}) => {
  const list = Array.isArray(questions) ? questions : [];

  const buildQuestionBlocks = (q, position) => {
    const blocks = [];
    blocks.push({
      text: `Question ${position + 1}: ${q.question}`,
      bold: true,
      spacingBeforeTwips: position === 0 ? 0 : 240,
    });

    const hasAnswer = Array.isArray(q.answer) && q.answer.length > 0;

    if (includeAnswers && hasAnswer) {
      blocks.push({ text: 'Answer:', bold: true, spacingBeforeTwips: 80 });
      q.answer.forEach((ans, i) => {
        blocks.push({ text: `${i + 1}. ${ans}` });
      });
    } else if (!hasAnswer) {
      // Not answered yet - leave room to write the answer by hand.
      blocks.push({ text: '', spacingAfterTwips: HALF_PAGE_TWIPS });
    }

    return blocks;
  };

  const buildHeadingBlocks = (dateLabel, pageBreakBefore) => {
    const blocks = [
      {
        text: heading,
        bold: true,
        size: 28,
        spacingAfterTwips: dateLabel ? 40 : 240,
        pageBreakBefore,
      },
    ];
    if (dateLabel) {
      blocks.push({ text: dateLabel, bold: true, spacingAfterTwips: 240 });
    }
    return blocks;
  };

  if (!includeAnswers || !dateWise) {
    const blocks = [...buildHeadingBlocks(null, false)];
    list.forEach((q, idx) => blocks.push(...buildQuestionBlocks(q, idx)));
    return blocks;
  }

  // Date-wise: only questions with a recorded answer AND an answerDate participate.
  const dated = list.filter((q) => Array.isArray(q.answer) && q.answer.length > 0 && q.answerDate);

  if (selectedDate !== ALL_DATES_OPTION) {
    const matching = dated.filter((q) => formatAnswerDateLabel(q.answerDate) === selectedDate);
    const blocks = [...buildHeadingBlocks(selectedDate, false)];
    matching.forEach((q, idx) => blocks.push(...buildQuestionBlocks(q, idx)));
    return blocks;
  }

  const uniqueDates = getUniqueAnswerDates(dated);
  const blocks = [];

  uniqueDates.forEach(({ raw, label }, groupIdx) => {
    const groupQuestions = dated.filter((q) => q.answerDate === raw);
    blocks.push(...buildHeadingBlocks(label, groupIdx > 0));
    groupQuestions.forEach((q, idx) => blocks.push(...buildQuestionBlocks(q, idx)));
  });

  return blocks;
};

// Triggers a browser download of the given File/Blob without navigating away.
export const downloadFile = (file) => {
  const url = window.URL.createObjectURL(file);
  const link = document.createElement('a');
  link.href = url;
  link.download = file.name || 'document.docx';
  link.style.display = 'none';

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  window.URL.revokeObjectURL(url);
};
